import {
    useContext,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IChartValueProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    TrendingDown as TrendingDownIcon,
    TrendingUp as TrendingUpIcon,
    Minus as MinusIcon
} from "lucide-react-native";
import {
    STAT_TILE_DELTA_FRACTION_DIGITS,
    getStatTileDeltaDirection,
    getStatTileDeltaType
} from "../../../statTile/components/delta/stylesheet";
import {
    formatPercent
} from "../../../../helpers/locale";
import {
    ChartContext
} from "../../context";
import Badge from "../../../badge";
import Text from "../../../text";

const CHART_VALUE_DELTA_LABELS = {
    unchanged: "stat-tile-unchanged",
    decrease: "stat-tile-decrease",
    increase: "stat-tile-increase"
} as const;

const CHART_VALUE_DELTA_ICONS = {
    decrease: TrendingDownIcon,
    increase: TrendingUpIcon,
    unchanged: MinusIcon
};

const ChartValue: FC<IChartValueProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    isDeltaInverted = false,
    deltaFormatter,
    description,
    delta,
    style,
    value,
    ...props
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalizeProp ?? context?.customLocalize);

    const {
        container: containerDynamicStyle
    } = useStyles({
        spaces
    });

    const renderDelta = () => {
        if(delta === undefined || !Number.isFinite(delta)) {
            return null;
        }

        const direction = getStatTileDeltaDirection({
            value: delta
        });

        const DirectionIcon = CHART_VALUE_DELTA_ICONS[direction];

        const text = deltaFormatter ? deltaFormatter(delta) : formatPercent({
            options: {
                maximumFractionDigits: STAT_TILE_DELTA_FRACTION_DIGITS,
                minimumFractionDigits: STAT_TILE_DELTA_FRACTION_DIGITS,
                signDisplay: "exceptZero"
            },
            locale: activeLocale,
            value: delta
        }).replace("-", "−");

        return <View>
            <Badge
                accessibilityLabel={`${localize(CHART_VALUE_DELTA_LABELS[direction])}: ${text}`}
                type={getStatTileDeltaType({
                    isIncreaseGood: !isDeltaInverted,
                    value: delta
                })}
                titleStyle={stylesheet.badge}
                customTheme={customTheme}
                icon={({
                    customColor,
                    size
                }) => <DirectionIcon
                    color={customColor}
                    size={size}
                />}
                iconPosition="start"
                variant="tonal"
                size="small"
                title={text}
                role="img"
            />
        </View>;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <Text
            variant="headlineMediumSize"
            customTheme={customTheme}
            color="high"
        >
            {value}
        </Text>
        {renderDelta()}
        {
            description ?
                <Text
                    style={stylesheet.description}
                    customTheme={customTheme}
                    variant="bodySmallSize"
                    color="low"
                >
                    {description}
                </Text>
            :
                null
        }
    </View>;
};
export default ChartValue;
